import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Tab, Nav, Form, Table, Badge, Modal, Alert, Spinner } from 'react-bootstrap';
import { 
  FaUpload, 
  FaDownload, 
  FaTrashAlt, 
  FaEye, 
  FaMap, 
  FaFileAlt,
  FaBook,
  FaBuilding,
  FaEdit,
  FaCheck,
  FaTimesCircle,
  FaPlus,
  FaInfoCircle,
  FaImage,
  FaClipboardList,
  FaFileContract,
  FaWpforms,
  FaFolderOpen
} from 'react-icons/fa';
import supabase from '../../supabaseClient'; // Ajuste o caminho conforme sua estrutura
import '../../styles/dashboard.css';

const GestaoMaterialApoio = () => {
  // Estados para armazenar os dados
  const [plantas, setPlantas] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [activeTab, setActiveTab] = useState('plantas');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Estados para formulários
  const [novaPlanta, setNovaPlanta] = useState({
    titulo: '',
    descricao: '',
    arquivo: null,
    ativo: true,
    evento_id: ''
  });
  
  const [novoMaterial, setNovoMaterial] = useState({
    titulo: '',
    descricao: '',
    tipo: 'manual',
    arquivo: null,
    ativo: true,
    evento_id: ''
  });
  
  // Estado para modais
  const [showPlantaModal, setShowPlantaModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditMaterialModal, setShowEditMaterialModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [plantaToEdit, setPlantaToEdit] = useState(null);
  const [materialToEdit, setMaterialToEdit] = useState(null);
  
  // Estado para previews
  const [previewUrl, setPreviewUrl] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Estado para alertas
  const [alert, setAlert] = useState({ show: false, variant: '', message: '' });
  
  // Lista de eventos disponíveis
  const [eventos, setEventos] = useState([]);
  
  // Tipos de materiais de apoio
  const tiposMateriais = [
    { id: 'manual', nome: 'Manual do Expositor', icon: FaBook },
    { id: 'regulamento', nome: 'Regulamento', icon: FaFileContract },
    { id: 'formulario', nome: 'Formulário', icon: FaWpforms },
    { id: 'checklist', nome: 'Checklist', icon: FaClipboardList },
    { id: 'outros', nome: 'Outros Documentos', icon: FaFolderOpen }
  ];

  // Função para sanitizar nome do arquivo
  const sanitizeFileName = (fileName) => {
    // Remove ou substitui caracteres problemáticos
    return fileName
      .normalize('NFD') // Normaliza caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-zA-Z0-9._-]/g, '_') // Substitui caracteres especiais por underscore
      .replace(/_{2,}/g, '_') // Remove underscores duplos
      .replace(/^_+|_+$/g, '') // Remove underscores do início e fim
      .toLowerCase(); // Converte para minúsculas
  };

  // Função para mostrar alertas
  const showAlert = (variant, message) => {
    setAlert({
      show: true,
      variant,
      message
    });
    
    setTimeout(() => {
      setAlert({ show: false, variant: '', message: '' });
    }, 5000);
  };

  // Função para verificar se o arquivo é uma imagem
  const isImageFile = (url, formato) => {
    if (!url) return false;
    const imageFormats = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'];
    return imageFormats.includes(formato?.toUpperCase()) || 
           /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  };

  // Função para renderizar preview do arquivo
  const renderFilePreview = (arquivo_url, formato, arquivo_nome) => {
    if (!arquivo_url) {
      return (
        <div className="text-center py-5 bg-light rounded">
          <FaFileAlt size={48} className="text-muted mb-2" />
          <p className="text-muted">Nenhum arquivo disponível</p>
        </div>
      );
    }

    if (isImageFile(arquivo_url, formato)) {
      return (
        <div className="position-relative">
          <img 
            src={arquivo_url} 
            alt={arquivo_nome || 'Arquivo'}
            className="img-fluid rounded"
            style={{ 
              maxHeight: '300px', 
              width: '100%', 
              objectFit: 'contain',
              backgroundColor: '#f8f9fa'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div 
            className="text-center py-5 bg-light rounded" 
            style={{ display: 'none' }}
          >
            <FaFileAlt size={48} className="text-muted mb-2" />
            <p className="text-muted">Erro ao carregar imagem</p>
          </div>
        </div>
      );
    } else {
      // Para PDFs e outros arquivos
      return (
        <div className="text-center py-5 bg-light rounded">
          <FaFileAlt size={48} className="text-primary mb-2" />
          <p className="mb-2"><strong>{arquivo_nome}</strong></p>
          <p className="text-muted">Arquivo PDF - Clique em "Visualizar" para abrir</p>
        </div>
      );
    }
  };

  // Carregar eventos do Supabase
  const carregarEventos = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('id, nome, status, data_inicio, data_fim, ativo')
        .eq('ativo', true)
        .in('status', ['ativo', 'pendente', 'em_andamento']) 
        .order('data_inicio', { ascending: false });

      if (error) throw error;
      
      console.log('Eventos carregados:', data);
      setEventos(data || []);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      showAlert('danger', 'Erro ao carregar eventos: ' + error.message);
    }
  };

  // Carregar plantas do Supabase
  const carregarPlantas = async () => {
    setLoading(true);
    try {
      // Buscar plantas sem JOIN primeiro
      const { data: plantasData, error: plantasError } = await supabase
        .from('caex_plantas_evento')
        .select(`
          id,
          titulo,
          descricao,
          arquivo_url,
          arquivo_nome,
          tamanho,
          formato,
          data_upload,
          evento_id,
          ativo,
          created_at,
          updated_at
        `)
        .order('created_at', { ascending: false });

      if (plantasError) throw plantasError;

      // Buscar eventos relacionados separadamente
      const eventosIds = [...new Set(plantasData.map(p => p.evento_id))];
      const { data: eventosData, error: eventosError } = await supabase
        .from('caex_eventos')
        .select('id, nome, status, data_inicio, data_fim')
        .in('id', eventosIds);

      if (eventosError) {
        console.warn('Erro ao carregar eventos:', eventosError);
        // Continuar sem os dados dos eventos
      }

      // Criar mapa de eventos
      const eventosMap = {};
      if (eventosData) {
        eventosData.forEach(evento => {
          eventosMap[evento.id] = evento;
        });
      }

      // Processar os dados para o formato esperado pelo componente
      const plantasFormatadas = plantasData.map(planta => ({
        ...planta,
        evento_nome: eventosMap[planta.evento_id]?.nome || 'Evento não encontrado',
        evento_status: eventosMap[planta.evento_id]?.status || 'indefinido',
        data_upload: new Date(planta.data_upload).toLocaleDateString('pt-BR')
      }));

      setPlantas(plantasFormatadas);
    } catch (error) {
      console.error('Erro ao carregar plantas:', error);
      showAlert('danger', 'Erro ao carregar plantas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Carregar materiais de apoio do Supabase
  const carregarMateriais = async () => {
    setLoading(true);
    try {
      // Buscar materiais
      const { data: materiaisData, error: materiaisError } = await supabase
        .from('caex_materiais_apoio')
        .select(`
          id,
          titulo,
          descricao,
          tipo,
          arquivo_url,
          arquivo_nome,
          tamanho,
          formato,
          data_upload,
          evento_id,
          ativo,
          created_at,
          updated_at
        `)
        .order('created_at', { ascending: false });

      if (materiaisError) throw materiaisError;

      // Buscar eventos relacionados separadamente
      const eventosIds = [...new Set(materiaisData.map(m => m.evento_id))];
      const { data: eventosData, error: eventosError } = await supabase
        .from('caex_eventos')
        .select('id, nome, status, data_inicio, data_fim')
        .in('id', eventosIds);

      if (eventosError) {
        console.warn('Erro ao carregar eventos:', eventosError);
      }

      // Criar mapa de eventos
      const eventosMap = {};
      if (eventosData) {
        eventosData.forEach(evento => {
          eventosMap[evento.id] = evento;
        });
      }

      // Processar os dados para o formato esperado pelo componente
      const materiaisFormatados = materiaisData.map(material => ({
        ...material,
        evento_nome: eventosMap[material.evento_id]?.nome || 'Evento não encontrado',
        evento_status: eventosMap[material.evento_id]?.status || 'indefinido',
        data_upload: new Date(material.data_upload).toLocaleDateString('pt-BR'),
        tipo_nome: tiposMateriais.find(t => t.id === material.tipo)?.nome || material.tipo
      }));

      setMateriais(materiaisFormatados);
    } catch (error) {
      console.error('Erro ao carregar materiais:', error);
      showAlert('danger', 'Erro ao carregar materiais: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Upload de arquivo para o Supabase Storage - VERSÃO CORRIGIDA
  const uploadArquivo = async (arquivo, pasta = 'plantas') => {
    try {
      // Sanitizar o nome do arquivo
      const nomeOriginal = arquivo.name;
      const nomeSanitizado = sanitizeFileName(nomeOriginal);
      
      // Gerar nome único com timestamp
      const timestamp = Date.now();
      const nomeArquivo = `${timestamp}_${nomeSanitizado}`;
      const caminhoArquivo = `${pasta}/${nomeArquivo}`;

      console.log('Upload - Nome original:', nomeOriginal);
      console.log('Upload - Nome sanitizado:', nomeSanitizado);
      console.log('Upload - Caminho final:', caminhoArquivo);

      const { data, error } = await supabase.storage
        .from('materiais-apoio') // Nome do bucket no Supabase Storage
        .upload(caminhoArquivo, arquivo, {
          onProgress: (progress) => {
            setUploadProgress((progress.loaded / progress.total) * 100);
          }
        });

      if (error) {
        console.error('Erro no upload:', error);
        throw error;
      }

      console.log('Upload bem-sucedido:', data);

      // Obter URL pública do arquivo
      const { data: urlData } = supabase.storage
        .from('materiais-apoio')
        .getPublicUrl(caminhoArquivo);

      return {
        url: urlData.publicUrl,
        path: caminhoArquivo,
        nome: nomeArquivo
      };
    } catch (error) {
      console.error('Erro detalhado no upload:', error);
      throw new Error(`Erro no upload: ${error.message}`);
    }
  };

  // Função para salvar nova planta no Supabase
  const handlePlantaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      // Debug: verificar autenticação
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Usuário atual:', user);
      
      // Se não houver usuário, fazer login anônimo (opcional)
      if (!user) {
        console.log('Nenhum usuário autenticado');
      }

      // Validações
      if (!novaPlanta.titulo || !novaPlanta.evento_id || !novaPlanta.arquivo) {
        throw new Error('Preencha todos os campos obrigatórios');
      }

      // Upload do arquivo
      const arquivoInfo = await uploadArquivo(novaPlanta.arquivo, 'plantas');

      // Calcular tamanho do arquivo
      const tamanhoMB = (novaPlanta.arquivo.size / (1024 * 1024)).toFixed(1);
      const formato = novaPlanta.arquivo.name.split('.').pop().toUpperCase();

      // Salvar dados da planta no banco
      const { data, error } = await supabase
        .from('caex_plantas_evento')
        .insert([
          {
            titulo: novaPlanta.titulo,
            descricao: novaPlanta.descricao || null,
            arquivo_url: arquivoInfo.url,
            arquivo_nome: arquivoInfo.nome,
            tamanho: `${tamanhoMB} MB`,
            formato: formato,
            data_upload: new Date().toISOString().split('T')[0],
            evento_id: novaPlanta.evento_id,
            ativo: novaPlanta.ativo
          }
        ])
        .select();

      if (error) throw error;

      // Recarregar lista de plantas
      await carregarPlantas();

      // Limpar formulário e fechar modal
      setNovaPlanta({
        titulo: '',
        descricao: '',
        arquivo: null,
        ativo: true,
        evento_id: ''
      });
      setShowPlantaModal(false);
      setUploadProgress(0);

      showAlert('success', 'Planta cadastrada com sucesso!');

    } catch (error) {
      console.error('Erro ao salvar planta:', error);
      showAlert('danger', 'Erro ao salvar planta: ' + error.message);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Função para salvar novo material no Supabase - VERSÃO CORRIGIDA
  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      // Validações
      if (!novoMaterial.titulo || !novoMaterial.evento_id || !novoMaterial.arquivo || !novoMaterial.tipo) {
        throw new Error('Preencha todos os campos obrigatórios');
      }

      console.log('Iniciando upload do material:', novoMaterial.arquivo.name);

      // Upload do arquivo com nome sanitizado
      const arquivoInfo = await uploadArquivo(novoMaterial.arquivo, 'materiais');

      // Calcular tamanho do arquivo
      const tamanhoMB = (novoMaterial.arquivo.size / (1024 * 1024)).toFixed(1);
      const formato = novoMaterial.arquivo.name.split('.').pop().toUpperCase();

      console.log('Upload concluído, salvando no banco...');

      // Salvar dados do material no banco
      const { data, error } = await supabase
        .from('caex_materiais_apoio')
        .insert([
          {
            titulo: novoMaterial.titulo,
            descricao: novoMaterial.descricao || null,
            tipo: novoMaterial.tipo,
            arquivo_url: arquivoInfo.url,
            arquivo_nome: arquivoInfo.nome,
            tamanho: `${tamanhoMB} MB`,
            formato: formato,
            data_upload: new Date().toISOString().split('T')[0],
            evento_id: novoMaterial.evento_id,
            ativo: novoMaterial.ativo
          }
        ])
        .select();

      if (error) throw error;

      console.log('Material salvo com sucesso:', data);

      // Recarregar lista de materiais
      await carregarMateriais();

      // Limpar formulário e fechar modal
      setNovoMaterial({
        titulo: '',
        descricao: '',
        tipo: 'manual',
        arquivo: null,
        ativo: true,
        evento_id: ''
      });
      setShowMaterialModal(false);
      setUploadProgress(0);

      showAlert('success', 'Material cadastrado com sucesso!');

    } catch (error) {
      console.error('Erro ao salvar material:', error);
      showAlert('danger', 'Erro ao salvar material: ' + error.message);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Função para editar planta
  const handleEditPlanta = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      let updateData = {
        titulo: plantaToEdit.titulo,
        descricao: plantaToEdit.descricao || null,
        evento_id: plantaToEdit.evento_id,
        ativo: plantaToEdit.ativo,
        updated_at: new Date().toISOString()
      };

      // Se um novo arquivo foi selecionado, fazer upload
      if (plantaToEdit.novoArquivo) {
        const arquivoInfo = await uploadArquivo(plantaToEdit.novoArquivo, 'plantas');
        const tamanhoMB = (plantaToEdit.novoArquivo.size / (1024 * 1024)).toFixed(1);
        const formato = plantaToEdit.novoArquivo.name.split('.').pop().toUpperCase();

        updateData = {
          ...updateData,
          arquivo_url: arquivoInfo.url,
          arquivo_nome: arquivoInfo.nome,
          tamanho: `${tamanhoMB} MB`,
          formato: formato
        };

        // Remover arquivo antigo do storage se existir
        if (plantaToEdit.arquivo_url && !plantaToEdit.arquivo_url.startsWith('data:')) {
          const urlParts = plantaToEdit.arquivo_url.split('/');
          const fileName = urlParts[urlParts.length - 1];
          const filePath = `plantas/${fileName}`;
          
          await supabase.storage
            .from('materiais-apoio')
            .remove([filePath]);
        }
      }

      // Atualizar dados da planta no banco
      const { error } = await supabase
        .from('caex_plantas_evento')
        .update(updateData)
        .eq('id', plantaToEdit.id);

      if (error) throw error;

      // Recarregar lista de plantas
      await carregarPlantas();

      // Fechar modal e limpar estado
      setShowEditModal(false);
      setPlantaToEdit(null);
      setUploadProgress(0);

      showAlert('success', 'Planta atualizada com sucesso!');

    } catch (error) {
      console.error('Erro ao atualizar planta:', error);
      showAlert('danger', 'Erro ao atualizar planta: ' + error.message);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Função para editar material
  const handleEditMaterial = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);

    try {
      let updateData = {
        titulo: materialToEdit.titulo,
        descricao: materialToEdit.descricao || null,
        tipo: materialToEdit.tipo,
        evento_id: materialToEdit.evento_id,
        ativo: materialToEdit.ativo,
        updated_at: new Date().toISOString()
      };

      // Se um novo arquivo foi selecionado, fazer upload
      if (materialToEdit.novoArquivo) {
        const arquivoInfo = await uploadArquivo(materialToEdit.novoArquivo, 'materiais');
        const tamanhoMB = (materialToEdit.novoArquivo.size / (1024 * 1024)).toFixed(1);
        const formato = materialToEdit.novoArquivo.name.split('.').pop().toUpperCase();

        updateData = {
          ...updateData,
          arquivo_url: arquivoInfo.url,
          arquivo_nome: arquivoInfo.nome,
          tamanho: `${tamanhoMB} MB`,
          formato: formato
        };

        // Remover arquivo antigo do storage se existir
        if (materialToEdit.arquivo_url && !materialToEdit.arquivo_url.startsWith('data:')) {
          const urlParts = materialToEdit.arquivo_url.split('/');
          const fileName = urlParts[urlParts.length - 1];
          const filePath = `materiais/${fileName}`;
          
          await supabase.storage
            .from('materiais-apoio')
            .remove([filePath]);
        }
      }

      // Atualizar dados do material no banco
      const { error } = await supabase
        .from('caex_materiais_apoio')
        .update(updateData)
        .eq('id', materialToEdit.id);

      if (error) throw error;

      // Recarregar lista de materiais
      await carregarMateriais();

      // Fechar modal e limpar estado
      setShowEditMaterialModal(false);
      setMaterialToEdit(null);
      setUploadProgress(0);

      showAlert('success', 'Material atualizado com sucesso!');

    } catch (error) {
      console.error('Erro ao atualizar material:', error);
      showAlert('danger', 'Erro ao atualizar material: ' + error.message);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Função para excluir planta
  const excluirPlanta = async (planta) => {
    setLoading(true);
    try {
      // Excluir arquivo do storage
      if (planta.arquivo_url && !planta.arquivo_url.startsWith('data:')) {
        // Extrair o path do arquivo da URL
        const urlParts = planta.arquivo_url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const filePath = `plantas/${fileName}`;
        
        await supabase.storage
          .from('materiais-apoio')
          .remove([filePath]);
      }

      // Excluir registro do banco
      const { error } = await supabase
        .from('caex_plantas_evento')
        .delete()
        .eq('id', planta.id);

      if (error) throw error;

      // Recarregar lista
      await carregarPlantas();
      showAlert('success', 'Planta excluída com sucesso!');

    } catch (error) {
      console.error('Erro ao excluir planta:', error);
      showAlert('danger', 'Erro ao excluir planta: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir material
  const excluirMaterial = async (material) => {
    setLoading(true);
    try {
      // Excluir arquivo do storage
      if (material.arquivo_url && !material.arquivo_url.startsWith('data:')) {
        // Extrair o path do arquivo da URL
        const urlParts = material.arquivo_url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const filePath = `materiais/${fileName}`;
        
        await supabase.storage
          .from('materiais-apoio')
          .remove([filePath]);
      }

      // Excluir registro do banco
      const { error } = await supabase
        .from('caex_materiais_apoio')
        .delete()
        .eq('id', material.id);

      if (error) throw error;

      // Recarregar lista
      await carregarMateriais();
      showAlert('success', 'Material excluído com sucesso!');

    } catch (error) {
      console.error('Erro ao excluir material:', error);
      showAlert('danger', 'Erro ao excluir material: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Função para alternar status ativo/inativo da planta
  const toggleStatusPlanta = async (id, novoStatus) => {
    try {
      const { error } = await supabase
        .from('caex_plantas_evento')
        .update({ 
          ativo: novoStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Recarregar lista
      await carregarPlantas();
      showAlert('success', 'Status atualizado com sucesso!');

    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showAlert('danger', 'Erro ao atualizar status: ' + error.message);
    }
  };

  // Função para alternar status ativo/inativo do material
  const toggleStatusMaterial = async (id, novoStatus) => {
    try {
      const { error } = await supabase
        .from('caex_materiais_apoio')
        .update({ 
          ativo: novoStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Recarregar lista
      await carregarMateriais();
      showAlert('success', 'Status atualizado com sucesso!');

    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showAlert('danger', 'Erro ao atualizar status: ' + error.message);
    }
  };

  // Carregar dados quando o componente monta
  useEffect(() => {
    carregarEventos();
    carregarPlantas();
    carregarMateriais();
  }, []);

  // Função para lidar com mudanças nos inputs de planta
  const handlePlantaChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setNovaPlanta({
        ...novaPlanta,
        arquivo: files[0]
      });
    } else if (type === 'checkbox') {
      setNovaPlanta({
        ...novaPlanta,
        [name]: checked
      });
    } else {
      setNovaPlanta({
        ...novaPlanta,
        [name]: value
      });
    }
  };

  // Função para lidar com mudanças nos inputs de material
  const handleMaterialChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setNovoMaterial({
        ...novoMaterial,
        arquivo: files[0]
      });
    } else if (type === 'checkbox') {
      setNovoMaterial({
        ...novoMaterial,
        [name]: checked
      });
    } else {
      setNovoMaterial({
        ...novoMaterial,
        [name]: value
      });
    }
  };

  // Função para lidar com mudanças nos inputs de edição de planta
  const handleEditPlantaChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setPlantaToEdit({
        ...plantaToEdit,
        novoArquivo: files[0]
      });
    } else if (type === 'checkbox') {
      setPlantaToEdit({
        ...plantaToEdit,
        [name]: checked
      });
    } else {
      setPlantaToEdit({
        ...plantaToEdit,
        [name]: value
      });
    }
  };

  // Função para lidar com mudanças nos inputs de edição de material
  const handleEditMaterialChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setMaterialToEdit({
        ...materialToEdit,
        novoArquivo: files[0]
      });
    } else if (type === 'checkbox') {
      setMaterialToEdit({
        ...materialToEdit,
        [name]: checked
      });
    } else {
      setMaterialToEdit({
        ...materialToEdit,
        [name]: value
      });
    }
  };

  // Função para visualizar arquivo
  const handlePreview = (url) => {
    setPreviewUrl(url);
    setShowPreview(true);
  };

  // Função para iniciar edição
  const handleEditInit = (planta) => {
    setPlantaToEdit({
      ...planta,
      novoArquivo: null
    });
    setShowEditModal(true);
  };

  // Função para iniciar edição de material
  const handleEditMaterialInit = (material) => {
    setMaterialToEdit({
      ...material,
      novoArquivo: null
    });
    setShowEditMaterialModal(true);
  };

  // Função para iniciar exclusão
  const handleDeleteInit = (item, type) => {
    setItemToDelete({ ...item, type });
    setShowDeleteModal(true);
  };

  // Função para confirmar exclusão
  const handleDeleteConfirm = async () => {
    if (itemToDelete.type === 'planta') {
      await excluirPlanta(itemToDelete);
    } else if (itemToDelete.type === 'material') {
      await excluirMaterial(itemToDelete);
    }
    
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Função para obter ícone do tipo de material
  const getTipoIcon = (tipo) => {
    const tipoInfo = tiposMateriais.find(t => t.id === tipo);
    const IconComponent = tipoInfo?.icon || FaFileAlt;
    return <IconComponent className="text-primary" />;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaFileAlt className="dashboard-title-icon" />
              Gestão de Materiais de Apoio
            </h1>
            <p className="dashboard-subtitle">
              Gerencie plantas do evento e documentos de apoio
            </p>
          </div>
        </div>
        
        {/* Alerta de sucesso/erro */}
        {alert.show && (
          <Alert 
            variant={alert.variant} 
            onClose={() => setAlert({ show: false, variant: '', message: '' })} 
            dismissible
            className="mb-4"
          >
            {alert.message}
          </Alert>
        )}
        
        {/* Loading Spinner */}
        {loading && (
          <div className="text-center mb-3">
            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
            Carregando...
            {uploadProgress > 0 && (
              <div className="mt-2">
                <div className="progress" style={{ height: '5px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <small>Upload: {Math.round(uploadProgress)}%</small>
              </div>
            )}
          </div>
        )}
        
        {/* Tabs para Plantas e Materiais */}
        <Tab.Container id="material-apoio-tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Card className="modern-table-card mb-4">
            <Card.Header className="modern-card-header">
              <Nav variant="tabs">
                <Nav.Item>
                  <Nav.Link eventKey="plantas" className="d-flex align-items-center gap-2">
                    <FaMap /> Plantas do Evento
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="materiais" className="d-flex align-items-center gap-2">
                    <FaBook /> Materiais de Apoio
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body className="modern-card-body">
              <Tab.Content>
                {/* Tab de Plantas */}
                <Tab.Pane eventKey="plantas">
                  <div className="mb-3 d-flex justify-content-between align-items-center">
                    <h5 className="m-0">Plantas Cadastradas ({plantas.length})</h5>
                    <Button 
                      variant="primary" 
                      className="d-flex align-items-center gap-2"
                      onClick={() => setShowPlantaModal(true)}
                      disabled={loading}
                    >
                      <FaPlus /> Nova Planta
                    </Button>
                  </div>
                  
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{width: '30%'}}>Título</th>
                          <th style={{width: '20%'}}>Evento</th>
                          <th style={{width: '15%'}}>Data</th>
                          <th style={{width: '10%'}}>Tamanho</th>
                          <th style={{width: '10%'}}>Status</th>
                          <th style={{width: '15%'}}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plantas.length > 0 ? (
                          plantas.map(planta => (
                            <tr key={planta.id}>
                              <td>
                                <div className="d-flex flex-column">
                                  <span className="fw-medium">{planta.titulo}</span>
                                  <small className="text-muted">{planta.descricao}</small>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <span>{planta.evento_nome}</span>
                                  <small className="text-muted">
                                    Status: 
                                    <Badge 
                                      bg={
                                        planta.evento_status === 'ativo' ? 'success' :
                                        planta.evento_status === 'em_andamento' ? 'warning' :
                                        planta.evento_status === 'pendente' ? 'info' : 'secondary'
                                      }
                                      className="ms-1"
                                    >
                                      {planta.evento_status}
                                    </Badge>
                                  </small>
                                </div>
                              </td>
                              <td>{planta.data_upload}</td>
                              <td>{planta.tamanho}</td>
                              <td>
                                <Badge bg={planta.ativo ? 'success' : 'secondary'}>
                                  {planta.ativo ? 'Ativo' : 'Inativo'}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    title="Editar"
                                    onClick={() => handleEditInit(planta)}
                                    disabled={loading}
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button 
                                    variant="outline-success" 
                                    size="sm"
                                    title={planta.ativo ? 'Desativar' : 'Ativar'}
                                    onClick={() => toggleStatusPlanta(planta.id, !planta.ativo)}
                                    disabled={loading}
                                  >
                                    {planta.ativo ? <FaTimesCircle /> : <FaCheck />}
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    title="Excluir"
                                    onClick={() => handleDeleteInit(planta, 'planta')}
                                    disabled={loading}
                                  >
                                    <FaTrashAlt />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-4">
                              {loading ? 'Carregando plantas...' : 'Nenhuma planta cadastrada'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab.Pane>
                
                {/* Tab de Materiais de Apoio */}
                <Tab.Pane eventKey="materiais">
                  <div className="mb-3 d-flex justify-content-between align-items-center">
                    <h5 className="m-0">Materiais Cadastrados ({materiais.length})</h5>
                    <Button 
                      variant="primary" 
                      className="d-flex align-items-center gap-2"
                      onClick={() => setShowMaterialModal(true)}
                      disabled={loading}
                    >
                      <FaPlus /> Novo Material
                    </Button>
                  </div>
                  
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{width: '25%'}}>Título</th>
                          <th style={{width: '15%'}}>Tipo</th>
                          <th style={{width: '20%'}}>Evento</th>
                          <th style={{width: '10%'}}>Data</th>
                          <th style={{width: '10%'}}>Tamanho</th>
                          <th style={{width: '10%'}}>Status</th>
                          <th style={{width: '10%'}}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materiais.length > 0 ? (
                          materiais.map(material => (
                            <tr key={material.id}>
                              <td>
                                <div className="d-flex flex-column">
                                  <span className="fw-medium">{material.titulo}</span>
                                  <small className="text-muted">{material.descricao}</small>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {getTipoIcon(material.tipo)}
                                  <span>{material.tipo_nome}</span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <span>{material.evento_nome}</span>
                                  <small className="text-muted">
                                    Status: 
                                    <Badge 
                                      bg={
                                        material.evento_status === 'ativo' ? 'success' :
                                        material.evento_status === 'em_andamento' ? 'warning' :
                                        material.evento_status === 'pendente' ? 'info' : 'secondary'
                                      }
                                      className="ms-1"
                                    >
                                      {material.evento_status}
                                    </Badge>
                                  </small>
                                </div>
                              </td>
                              <td>{material.data_upload}</td>
                              <td>{material.tamanho}</td>
                              <td>
                                <Badge bg={material.ativo ? 'success' : 'secondary'}>
                                  {material.ativo ? 'Ativo' : 'Inativo'}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    title="Editar"
                                    onClick={() => handleEditMaterialInit(material)}
                                    disabled={loading}
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button 
                                    variant="outline-success" 
                                    size="sm"
                                    title={material.ativo ? 'Desativar' : 'Ativar'}
                                    onClick={() => toggleStatusMaterial(material.id, !material.ativo)}
                                    disabled={loading}
                                  >
                                    {material.ativo ? <FaTimesCircle /> : <FaCheck />}
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    title="Excluir"
                                    onClick={() => handleDeleteInit(material, 'material')}
                                    disabled={loading}
                                  >
                                    <FaTrashAlt />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              {loading ? 'Carregando materiais...' : 'Nenhum material cadastrado'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </Tab.Container>
        
        {/* Dicas e Informações */}
        <Card className="modern-table-card mb-4">
          <Card.Header className="modern-card-header">
            <h5 className="m-0 d-flex align-items-center gap-2">
              <FaInfoCircle /> Dicas para Upload de Materiais
            </h5>
          </Card.Header>
          <Card.Body className="modern-card-body">
            <Row>
              <Col md={6}>
                <h6>Plantas do Evento</h6>
                <ul className="small text-muted">
                  <li>Use arquivos no formato PDF para melhor compatibilidade</li>
                  <li>Resoluções recomendadas: 300 DPI para impressão, 150 DPI para visualização digital</li>
                  <li>Tamanho máximo de arquivo: 20 MB</li>
                  <li>Inclua medidas e legendas para melhor compreensão</li>
                </ul>
              </Col>
              <Col md={6}>
                <h6>Materiais de Apoio</h6>
                <ul className="small text-muted">
                  <li>O Manual do Expositor deve conter todas as informações essenciais</li>
                  <li>Verifique as informações sobre prazos antes de publicar</li>
                  <li>Categorize corretamente os documentos para facilitar a busca</li>
                  <li>Desative versões antigas quando publicar atualizações</li>
                </ul>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
      
      {/* Modal para cadastrar nova planta */}
      <Modal show={showPlantaModal} onHide={() => setShowPlantaModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Nova Planta</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePlantaSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Título *</Form.Label>
              <Form.Control 
                type="text" 
                name="titulo" 
                value={novaPlanta.titulo} 
                onChange={handlePlantaChange} 
                required 
                disabled={loading}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                name="descricao" 
                value={novaPlanta.descricao} 
                onChange={handlePlantaChange} 
                disabled={loading}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Evento *</Form.Label>
              <Form.Select 
                name="evento_id" 
                value={novaPlanta.evento_id} 
                onChange={handlePlantaChange} 
                required
                disabled={loading}
              >
                <option value="">Selecione um evento</option>
                {eventos.map(evento => (
                  <option key={evento.id} value={evento.id}>
                    {evento.nome} 
                    {evento.status && ` (${evento.status})`}
                    {evento.data_inicio && ` - ${new Date(evento.data_inicio).toLocaleDateString('pt-BR')}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Arquivo da Planta *</Form.Label>
              <Form.Control 
                type="file" 
                name="arquivo" 
                onChange={handlePlantaChange} 
                accept=".pdf,.jpg,.jpeg,.png" 
                required 
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Formatos aceitos: PDF, JPG, PNG. Tamanho máximo: 20MB.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check 
                type="checkbox" 
                label="Ativo" 
                name="ativo" 
                checked={novaPlanta.ativo} 
                onChange={handlePlantaChange} 
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Se marcado, a planta ficará visível para os expositores.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowPlantaModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading || !novaPlanta.titulo || !novaPlanta.evento_id || !novaPlanta.arquivo}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Cadastrando...
                </>
              ) : (
                'Cadastrar Planta'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para cadastrar novo material */}
      <Modal show={showMaterialModal} onHide={() => setShowMaterialModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Novo Material</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleMaterialSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Título *</Form.Label>
              <Form.Control 
                type="text" 
                name="titulo" 
                value={novoMaterial.titulo} 
                onChange={handleMaterialChange} 
                required 
                disabled={loading}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3} 
                name="descricao" 
                value={novoMaterial.descricao} 
                onChange={handleMaterialChange} 
                disabled={loading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tipo de Material *</Form.Label>
              <Form.Select 
                name="tipo" 
                value={novoMaterial.tipo} 
                onChange={handleMaterialChange} 
                required
                disabled={loading}
              >
                {tiposMateriais.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nome}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Evento *</Form.Label>
              <Form.Select 
                name="evento_id" 
                value={novoMaterial.evento_id} 
                onChange={handleMaterialChange} 
                required
                disabled={loading}
              >
                <option value="">Selecione um evento</option>
                {eventos.map(evento => (
                  <option key={evento.id} value={evento.id}>
                    {evento.nome} 
                    {evento.status && ` (${evento.status})`}
                    {evento.data_inicio && ` - ${new Date(evento.data_inicio).toLocaleDateString('pt-BR')}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Arquivo do Material *</Form.Label>
              <Form.Control 
                type="file" 
                name="arquivo" 
                onChange={handleMaterialChange} 
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" 
                required 
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Formatos aceitos: PDF, DOC, DOCX, JPG, PNG. Tamanho máximo: 20MB.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check 
                type="checkbox" 
                label="Ativo" 
                name="ativo" 
                checked={novoMaterial.ativo} 
                onChange={handleMaterialChange} 
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Se marcado, o material ficará visível para os expositores.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowMaterialModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading || !novoMaterial.titulo || !novoMaterial.evento_id || !novoMaterial.arquivo || !novoMaterial.tipo}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Cadastrando...
                </>
              ) : (
                'Cadastrar Material'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* Modal para editar planta */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Planta</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditPlanta}>
          <Modal.Body>
            {plantaToEdit && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Título *</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="titulo" 
                    value={plantaToEdit.titulo} 
                    onChange={handleEditPlantaChange} 
                    required 
                    disabled={loading}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={3} 
                    name="descricao" 
                    value={plantaToEdit.descricao || ''} 
                    onChange={handleEditPlantaChange} 
                    disabled={loading}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Evento *</Form.Label>
                  <Form.Select 
                    name="evento_id" 
                    value={plantaToEdit.evento_id} 
                    onChange={handleEditPlantaChange} 
                    required
                    disabled={loading}
                  >
                    <option value="">Selecione um evento</option>
                    {eventos.map(evento => (
                      <option key={evento.id} value={evento.id}>
                        {evento.nome} 
                        {evento.status && ` (${evento.status})`}
                        {evento.data_inicio && ` - ${new Date(evento.data_inicio).toLocaleDateString('pt-BR')}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                {/* Seção de Imagem da Planta */}
                <Form.Group className="mb-3">
                  <Form.Label>Imagem da Planta</Form.Label>
                  <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                    {renderFilePreview(plantaToEdit.arquivo_url, plantaToEdit.formato, plantaToEdit.arquivo_nome)}
                    
                    <div className="text-center mt-3">
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => document.getElementById('editFileInput').click()}
                        disabled={loading}
                        className="me-2"
                      >
                        <FaImage className="me-1" /> Alterar imagem
                      </Button>
                      
                      {plantaToEdit.arquivo_url && (
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handlePreview(plantaToEdit.arquivo_url)}
                        >
                          <FaEye className="me-1" /> Visualizar
                        </Button>
                      )}
                    </div>
                    
                    <input
                      id="editFileInput"
                      type="file"
                      name="novoArquivo"
                      onChange={handleEditPlantaChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      disabled={loading}
                    />
                    
                    <Form.Text className="text-muted d-block mt-2">
                      Deixe em branco para manter o arquivo atual. Formatos aceitos: PDF, JPG, PNG. Tamanho máximo: 20MB.
                    </Form.Text>
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="checkbox" 
                    label="Ativo" 
                    name="ativo" 
                    checked={plantaToEdit.ativo} 
                    onChange={handleEditPlantaChange} 
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    Se marcado, a planta ficará visível para os expositores.
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowEditModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading || !plantaToEdit?.titulo || !plantaToEdit?.evento_id}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para editar material */}
      <Modal show={showEditMaterialModal} onHide={() => setShowEditMaterialModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Material</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditMaterial}>
          <Modal.Body>
            {materialToEdit && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Título *</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="titulo" 
                    value={materialToEdit.titulo} 
                    onChange={handleEditMaterialChange} 
                    required 
                    disabled={loading}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={3} 
                    name="descricao" 
                    value={materialToEdit.descricao || ''} 
                    onChange={handleEditMaterialChange} 
                    disabled={loading}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Material *</Form.Label>
                  <Form.Select 
                    name="tipo" 
                    value={materialToEdit.tipo} 
                    onChange={handleEditMaterialChange} 
                    required
                    disabled={loading}
                  >
                    {tiposMateriais.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nome}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Evento *</Form.Label>
                  <Form.Select 
                    name="evento_id" 
                    value={materialToEdit.evento_id} 
                    onChange={handleEditMaterialChange} 
                    required
                    disabled={loading}
                  >
                    <option value="">Selecione um evento</option>
                    {eventos.map(evento => (
                      <option key={evento.id} value={evento.id}>
                        {evento.nome} 
                        {evento.status && ` (${evento.status})`}
                        {evento.data_inicio && ` - ${new Date(evento.data_inicio).toLocaleDateString('pt-BR')}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                {/* Seção de Arquivo do Material */}
                <Form.Group className="mb-3">
                  <Form.Label>Arquivo do Material</Form.Label>
                  <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                    {renderFilePreview(materialToEdit.arquivo_url, materialToEdit.formato, materialToEdit.arquivo_nome)}
                    
                    <div className="text-center mt-3">
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => document.getElementById('editMaterialFileInput').click()}
                        disabled={loading}
                        className="me-2"
                      >
                        <FaImage className="me-1" /> Alterar arquivo
                      </Button>
                      
                      {materialToEdit.arquivo_url && (
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handlePreview(materialToEdit.arquivo_url)}
                        >
                          <FaEye className="me-1" /> Visualizar
                        </Button>
                      )}
                    </div>
                    
                    <input
                      id="editMaterialFileInput"
                      type="file"
                      name="novoArquivo"
                      onChange={handleEditMaterialChange}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      disabled={loading}
                    />
                    
                    <Form.Text className="text-muted d-block mt-2">
                      Deixe em branco para manter o arquivo atual. Formatos aceitos: PDF, DOC, DOCX, JPG, PNG. Tamanho máximo: 20MB.
                    </Form.Text>
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Check 
                    type="checkbox" 
                    label="Ativo" 
                    name="ativo" 
                    checked={materialToEdit.ativo} 
                    onChange={handleEditMaterialChange} 
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    Se marcado, o material ficará visível para os expositores.
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowEditMaterialModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading || !materialToEdit?.titulo || !materialToEdit?.evento_id || !materialToEdit?.tipo}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* Modal de confirmação de exclusão */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {itemToDelete && (
            <p>
              Tem certeza que deseja excluir <strong>{itemToDelete.titulo}</strong>?
              <br />
              Esta ação não poderá ser desfeita e o arquivo será removido permanentemente.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Excluindo...
              </>
            ) : (
              'Excluir'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal de preview do arquivo */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Visualização do Arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ratio ratio-16x9">
            <iframe 
              src={previewUrl} 
              title="Visualização do arquivo"
              allowFullScreen
            ></iframe>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Fechar
          </Button>
          <Button 
            variant="primary" 
            href={previewUrl} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <FaDownload className="me-2" /> Baixar Arquivo
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestaoMaterialApoio;